import React, { useState } from "react";
import { Theme } from "../../../../providers/ThemeProvider";
import {
  SCORE_CATEGORY_LABELS,
  isHighLowMetric,
  getHighLowLabel,
  getCategoryPillTokens,
} from "../../store/helper";
import ArrowDown from "../../svg/ArrowDown";
import DoubleArrowDown from "../../svg/DoubleArrowDown";
import {
  ProcessedWeight,
  BiomarkerItem,
  BreathMetricGroup,
} from "./usePanelData";
import CustomTooltip from "../common/CustomTooltip";

// --- CategoryPill ---
// The category name on a tinted pill, sitting next to a plain-black value.
// Replaces the old "grey pill holding a colored value + grey category text"
// pairing across the side panel.
export type CategoryVariant = "score" | "biomarkers";

export const CategoryPill = ({
  label,
  scoreCategory,
  variant = "score",
  theme,
}: {
  label: string;
  scoreCategory: number;
  variant?: CategoryVariant;
  theme: Theme;
}) => {
  if (!label) return null;
  const tokens = getCategoryPillTokens(scoreCategory, theme, variant);
  return (
    <span style={sharedStyles.categoryPill(tokens.background, tokens.color)}>
      {label}
    </span>
  );
};

// --- PanelHeader ---
type PanelHeaderProps = {
  name: string;
  fullName?: string;
  value: number;
  unit: string;
  scoreCategory: number;
  categoryColor: string;
  categoryLabel?: string;
  /** Which category scale `scoreCategory` belongs to — see CategoryPill. */
  categoryVariant?: CategoryVariant;
  metricId?: string;
  onClose: () => void;
  theme: Theme;
};

export const PanelHeader = ({
  name,
  fullName,
  value,
  unit,
  scoreCategory,
  categoryColor,
  categoryLabel,
  categoryVariant = "score",
  metricId,
  onClose,
  theme,
}: PanelHeaderProps) => {
  const highLow = metricId && isHighLowMetric(metricId);
  return (
    <div style={sharedStyles.header}>
      <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
        <div style={sharedStyles.colorLine(categoryColor)} />
        <div style={sharedStyles.headerTextSection}>
          {fullName && fullName !== name ? (
            <CustomTooltip message={fullName} customHeight="auto">
              <div style={sharedStyles.headerName(theme)}>{name}</div>
            </CustomTooltip>
          ) : (
            <div style={sharedStyles.headerName(theme)}>{name}</div>
          )}
          <div style={sharedStyles.headerScoreRow}>
            <span style={sharedStyles.headerValue(theme)}>
              {highLow ? getHighLowLabel(value) : `${value} ${unit}`}
            </span>
            {!highLow && (
              <CategoryPill
                label={
                  categoryLabel ?? SCORE_CATEGORY_LABELS[scoreCategory || 1]
                }
                scoreCategory={scoreCategory}
                variant={categoryVariant}
                theme={theme}
              />
            )}
          </div>
        </div>
      </div>
      <div style={sharedStyles.closeButton} onClick={onClose}>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.21094 22.2903L21.7912 2.70996M2.21094 2.70996L21.7912 22.2903"
            stroke={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}
            strokeWidth="2"
            strokeMiterlimit="10"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
};

// --- InfoTextBox ---
type InfoTextBoxProps = {
  title: string;
  text: string;
  theme: Theme;
};

export const InfoTextBox = ({ title, text, theme }: InfoTextBoxProps) => (
  <div style={sharedStyles.infoTextBox(theme)}>
    <div style={sharedStyles.infoTextBoxTitle(theme)}>{title}</div>
    <div style={sharedStyles.infoTextBoxContent(theme)}>{text}</div>
  </div>
);

// --- TagChip ---
type TagChipVariant = "positive" | "negative" | "neutral";

type TagChipProps = {
  label: string;
  variant: TagChipVariant;
  theme: Theme;
  isSelected?: boolean;
  isDimmed?: boolean;
  onClick?: () => void;
};

export const TagChip = ({
  label,
  variant,
  theme,
  isSelected,
  isDimmed,
  onClick,
}: TagChipProps) => {
  const bgColor =
    variant === "positive"
      ? theme.colorTokens.S[10]
      : variant === "negative"
      ? theme.colorTokens.D[10]
      : theme.colorTokens.N[10];
  const textColor =
    variant === "positive"
      ? theme.colorTokens.S[60]
      : variant === "negative"
      ? theme.colorTokens.D[60]
      : theme.colors_ds.NEUTRAL_TEXT_DEFAULT;
  const borderColor =
    variant === "positive"
      ? theme.colorTokens.S[40]
      : variant === "negative"
      ? theme.colorTokens.D[40]
      : theme.colors_ds.NEUTRAL_BORDER_WEAK;

  return (
    <div
      style={{
        ...sharedStyles.tagChip(bgColor),
        ...(onClick ? { cursor: "pointer" } : {}),
        ...(isSelected
          ? { border: `1px solid ${borderColor}`, padding: "3px 7px" }
          : {}),
        ...(isDimmed ? { opacity: 0.4 } : {}),
      }}
      onClick={onClick}
    >
      <span style={sharedStyles.tagChipText(textColor)}>{label}</span>
    </div>
  );
};

// --- InfluencedBySection ---
// `description` is optional — the consultation surface drops it because its
// chips are read-only and never open a popup. The score screen still ships
// real descriptions.
type InfluencedByItem = { label: string; description?: string };

type InfluencedBySectionProps = {
  positive: InfluencedByItem[];
  negative: InfluencedByItem[];
  theme: Theme;
  /**
   * When true, chips render as static labels — no onClick wiring, no cursor
   * pointer, no description popup. Used by the consultation where the
   * descriptions aren't authored and the click-to-expand affordance would be
   * misleading (it would toggle state but reveal nothing). Defaults to false
   * so the score screen's interactive behavior is preserved.
   */
  readOnly?: boolean;
};

export const InfluencedBySection = ({
  positive,
  negative,
  theme,
  readOnly = false,
}: InfluencedBySectionProps) => {
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const allItems = [
    ...positive.map((item) => ({ ...item, variant: "positive" as const })),
    ...negative.map((item) => ({ ...item, variant: "negative" as const })),
  ];

  if (allItems.length === 0) return null;

  const hasSelection = !readOnly && selectedLabel !== null;
  const selectedItem = readOnly
    ? null
    : allItems.find((item) => item.label === selectedLabel);

  const handleTagClick = (label: string) => {
    setSelectedLabel((prev) => (prev === label ? null : label));
  };

  return (
    <div style={sharedStyles.tagsSection}>
      <div style={sharedStyles.tagsSectionHeader}>
        <span style={sharedStyles.tagsSectionTitle(theme)}>Influenced by</span>
      </div>
      <div style={sharedStyles.tagsWrap}>
        {allItems.map((item, idx) => (
          <TagChip
            key={idx}
            label={item.label}
            variant={item.variant}
            theme={theme}
            isSelected={!readOnly && selectedLabel === item.label}
            isDimmed={hasSelection && selectedLabel !== item.label}
            onClick={readOnly ? undefined : () => handleTagClick(item.label)}
          />
        ))}
      </div>
      {selectedItem?.description && (
        <div style={sharedStyles.influencedByDescription(theme)}>
          {selectedItem.description}
        </div>
      )}
    </div>
  );
};

// --- AffectsThisSection ---
// `description` optional — see InfluencedByItem above.
type AffectsThisItem = { label: string; description?: string };
type AffectsThisSectionProps = {
  positive: AffectsThisItem[];
  negative: AffectsThisItem[];
  theme: Theme;
  /** See `InfluencedBySectionProps.readOnly`. */
  readOnly?: boolean;
};

export const AffectsThisSection = ({
  positive,
  negative,
  theme,
  readOnly = false,
}: AffectsThisSectionProps) => {
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);

  const allItems = [
    ...positive.map((item) => ({ ...item, variant: "positive" as const })),
    ...negative.map((item) => ({ ...item, variant: "negative" as const })),
  ];

  if (allItems.length === 0) return null;

  const hasSelection = !readOnly && selectedLabel !== null;
  const selectedItem = readOnly
    ? null
    : allItems.find((item) => item.label === selectedLabel);

  const handleTagClick = (label: string) => {
    setSelectedLabel((prev) => (prev === label ? null : label));
  };

  return (
    <div style={sharedStyles.tagsSection}>
      <div style={sharedStyles.tagsSectionHeader}>
        <span style={sharedStyles.tagsSectionTitle(theme)}>Affects this</span>
      </div>
      <div style={sharedStyles.tagsWrap}>
        {allItems.map((item, idx) => (
          <TagChip
            key={idx}
            label={item.label}
            variant={item.variant}
            theme={theme}
            isSelected={!readOnly && selectedLabel === item.label}
            isDimmed={hasSelection && selectedLabel !== item.label}
            onClick={readOnly ? undefined : () => handleTagClick(item.label)}
          />
        ))}
      </div>
      {selectedItem?.description && (
        <div style={sharedStyles.influencedByDescription(theme)}>
          {selectedItem.description}
        </div>
      )}
    </div>
  );
};

// --- ScoreRow ---
type ScoreRowProps = {
  score: number;
  unit: string;
  scoreCategory: number;
  categoryColor: string;
  categoryLabel?: string;
  /** Which category scale `scoreCategory` belongs to — see CategoryPill. */
  categoryVariant?: CategoryVariant;
  metricId?: string;
  theme: Theme;
};

export const ScoreRow = ({
  score,
  unit,
  scoreCategory,
  categoryColor,
  categoryLabel,
  categoryVariant = "score",
  metricId,
  theme,
}: ScoreRowProps) => {
  const highLow = metricId && isHighLowMetric(metricId);
  return (
    <div style={sharedStyles.scoreRowContainer}>
      <span style={sharedStyles.scoreRowLabel(theme)}>Score:</span>
      <span style={sharedStyles.scoreRowValue(theme)}>
        {highLow ? getHighLowLabel(score) : `${score}${unit}`}
      </span>
      {!highLow && (
        <CategoryPill
          label={categoryLabel || SCORE_CATEGORY_LABELS[scoreCategory || 1]}
          scoreCategory={scoreCategory}
          variant={categoryVariant}
          theme={theme}
        />
      )}
    </div>
  );
};

// --- TagsSection ---
type TagsSectionProps = {
  title: string;
  subtitle?: string;
  items: { label: string }[];
  variant: TagChipVariant;
  theme: Theme;
};

export const TagsSection = ({
  title,
  subtitle,
  items,
  variant,
  theme,
}: TagsSectionProps) => {
  if (!items || items.length === 0) return null;
  return (
    <div style={sharedStyles.tagsSection}>
      <div style={sharedStyles.tagsSectionHeader}>
        <span style={sharedStyles.tagsSectionTitle(theme)}>{title}</span>
        {subtitle && (
          <span style={sharedStyles.tagsSectionSubtitle(theme)}>
            {subtitle}
          </span>
        )}
      </div>
      <div style={sharedStyles.tagsWrap}>
        {items.map((item, idx) => (
          <TagChip
            key={idx}
            label={item.label}
            variant={variant}
            theme={theme}
          />
        ))}
      </div>
    </div>
  );
};

// --- ExpandableInfoCard ---
type ExpandableInfoCardProps = {
  title: string;
  description: string;
  theme: Theme;
};

export const ExpandableInfoCard = ({
  title,
  description,
  theme,
}: ExpandableInfoCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      style={sharedStyles.expandableCard(theme, isExpanded)}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div style={sharedStyles.expandableCardHeader}>
        <div style={sharedStyles.expandableDotAndText}>
          <div style={sharedStyles.expandableDot(theme, isExpanded)} />
          <span style={sharedStyles.expandableTitle(theme)}>{title}</span>
        </div>
        <div
          style={{
            ...sharedStyles.arrowIcon,
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ArrowDown width={12} height={12} />
        </div>
      </div>
      {isExpanded && (
        <div style={sharedStyles.expandableDescription(theme)}>
          {description}
        </div>
      )}
    </div>
  );
};

// --- SectionTitle ---
type SectionTitleProps = {
  title: string;
  subtitle?: string;
  theme: Theme;
};

export const SectionTitle = ({ title, subtitle, theme }: SectionTitleProps) => (
  <div style={sharedStyles.sectionTitleWrap}>
    <span style={sharedStyles.sectionTitleText(theme)}>{title}</span>
    {subtitle && (
      <span style={sharedStyles.sectionSubtitleText(theme)}>{subtitle}</span>
    )}
  </div>
);

// --- ScoreMeaningSection ---
export const ScoreMeaningSection = ({
  items,
  theme,
}: {
  items: { label: string; description: string }[];
  theme: Theme;
}) => {
  if (items.length === 0) return null;

  return (
    <div style={sharedStyles.compositionSection}>
      <div style={sharedStyles.compositionTitle(theme)}>
        What does your score mean?
      </div>
      <div style={sharedStyles.cardsColumn}>
        {items.map((insight, index) => (
          <InsightCard key={index} insight={insight} theme={theme} />
        ))}
      </div>
    </div>
  );
};

// --- InsightCard ---
const InsightCard = ({
  insight,
  theme,
}: {
  insight: { label: string; description: string };
  theme: Theme;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      style={sharedStyles.insightCard(theme, isExpanded)}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div style={sharedStyles.insightCardHeader}>
        <div style={sharedStyles.insightDotAndText}>
          <div style={sharedStyles.insightDot(theme, isExpanded)} />
          <span style={sharedStyles.insightTitle(theme)}>{insight.label}</span>
        </div>
        <div
          style={{
            ...sharedStyles.arrowIcon,
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ArrowDown width={12} height={12} />
        </div>
      </div>
      {isExpanded && (
        <div style={sharedStyles.insightDescription(theme)}>
          {insight.description}
        </div>
      )}
    </div>
  );
};

// --- BiomarkerListItem ---
type BiomarkerListItemProps = {
  label: string;
  value?: number;
  unit?: string;
  weight?: number | null;
  weightMax?: number | null;
  weightColor?: string;
  theme: Theme;
};

export const BiomarkerListItem = ({
  label,
  value,
  unit,
  weight,
  weightMax,
  weightColor,
  theme,
}: BiomarkerListItemProps) => (
  <div style={sharedStyles.biomarkerItem(theme)}>
    <div style={sharedStyles.biomarkerInfo}>
      <div style={sharedStyles.biomarkerName(theme)}>{label}</div>
      {value != null && (
        <div style={sharedStyles.biomarkerValue(theme, weightColor)}>
          {value} {unit}
        </div>
      )}
    </div>
    {weight != null && weightMax != null && weightMax > 0 && (
      <div style={sharedStyles.biomarkerPercentage(theme)}>{weight}%</div>
    )}
  </div>
);

// --- BloodBiomarkersList ---
export const BloodBiomarkersList = ({
  items,
  theme,
}: {
  items: BiomarkerItem[];
  theme: Theme;
}) => {
  // Hide biomarkers where weight is 0
  const visible = items.filter((item) => item.weight !== 0);
  if (visible.length === 0) return null;

  return (
    <div style={sharedStyles.biomarkerList(theme)}>
      {visible.map((item, idx) => (
        <div key={idx}>
          <BiomarkerListItem
            label={item.label}
            value={item.value}
            unit={item.unit}
            weight={item.weight}
            weightMax={item.weightMax}
            weightColor={item.categoryColor}
            theme={theme}
          />
          {idx < visible.length - 1 && (
            <div style={sharedStyles.biomarkerSeparator(theme)} />
          )}
        </div>
      ))}
    </div>
  );
};

// --- BreathBiomarkersList ---
export const BreathBiomarkersList = ({
  groups,
  theme,
}: {
  groups: BreathMetricGroup[];
  theme: Theme;
}) => {
  // Hide groups where weight is 0 or have no biomarkers
  const filtered = groups.filter(
    (g) => g.biomarkers.length > 0 && g.weight !== 0
  );
  if (filtered.length === 0) return null;

  return (
    <div style={sharedStyles.biomarkerList(theme)}>
      {filtered.map((group, gIdx) => (
        <div key={gIdx}>
          {gIdx > 0 && <div style={sharedStyles.biomarkerSeparator(theme)} />}
          {group.biomarkers.length === 1 ? (
            <BiomarkerListItem
              label={group.biomarkers[0].label}
              value={group.biomarkers[0].value}
              unit={group.biomarkers[0].unit}
              weight={group.weight}
              weightMax={group.weightMax}
              weightColor={group.biomarkers[0].categoryColor}
              theme={theme}
            />
          ) : (
            <div style={sharedStyles.breathGroupCell(theme)}>
              <div style={sharedStyles.breathGroupBiomarkers}>
                {group.biomarkers.map((bio, bIdx) => (
                  <div key={bIdx} style={sharedStyles.breathGroupBiomarkerRow}>
                    <div style={sharedStyles.biomarkerName(theme)}>
                      {bio.label}
                    </div>
                    <div
                      style={sharedStyles.biomarkerValue(
                        theme,
                        bio.categoryColor
                      )}
                    >
                      {bio.value} {bio.unit}
                    </div>
                  </div>
                ))}
              </div>
              {group.weight != null &&
                group.weightMax != null &&
                group.weightMax > 0 && (
                  <div style={sharedStyles.biomarkerPercentage(theme)}>
                    {group.weight}%
                  </div>
                )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// --- WeightCard ---
type WeightCardProps = {
  weight: ProcessedWeight;
  theme: Theme;
};

export const WeightCard = ({ weight, theme }: WeightCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't render if all biomarkers were filtered out (zero-weight)
  const hasVisibleContent =
    (weight.variant === "other" &&
      weight.biomarkerItems?.some((item) => item.weight !== 0)) ||
    (weight.variant === "breath" &&
      weight.metricGroups?.some(
        (g) => g.biomarkers.length > 0 && g.weight !== 0
      ));

  if (!hasVisibleContent) return null;

  return (
    <div
      style={sharedStyles.weightCard(theme, isExpanded)}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div style={sharedStyles.weightCardInner}>
        <div style={sharedStyles.weightCardContent}>
          <div style={sharedStyles.weightCardTopRow}>
            <span style={sharedStyles.weightName(theme)}>{weight.name}</span>
            <span style={sharedStyles.weightPercentage(theme)}>
              {parseFloat(weight.total.toFixed(2))}
              <span style={sharedStyles.weightPercentageMax(theme)}>
                /{parseFloat(weight.weightMax.toFixed(2))}%
              </span>
            </span>
          </div>
          <div style={sharedStyles.progressBarBg(theme)}>
            <div
              style={sharedStyles.progressBarFill(
                theme,
                weight.weightMax > 0
                  ? (weight.total / weight.weightMax) * 100
                  : 0
              )}
            />
          </div>
        </div>
        <div
          style={{
            ...sharedStyles.arrowIcon,
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <ArrowDown width={16} height={16} />
        </div>
      </div>

      {isExpanded && (
        <div style={sharedStyles.expandedContent}>
          {weight.variant === "other" &&
            weight.biomarkerItems &&
            weight.biomarkerItems.length > 0 && (
              <BloodBiomarkersList
                items={weight.biomarkerItems}
                theme={theme}
              />
            )}

          {weight.variant === "breath" &&
            weight.metricGroups &&
            weight.metricGroups.some((g) => g.biomarkers.length > 0) && (
              <BreathBiomarkersList
                groups={weight.metricGroups}
                theme={theme}
              />
            )}
        </div>
      )}
    </div>
  );
};

// --- TimelineGroup ---
// "Influenced by" → score → "Affects this", connected by a vertical rail.
// Lives here (not in InfoPanelContent) because three surfaces render it: the
// score side panel, the consultation system detail, and the health overview
// biomarker panel — the last one is imported BY InfoPanelContent, so keeping
// the component there would create an import cycle.
type TimelineRow = {
  marker: "icon" | "score";
  categoryColor?: string;
  content: React.ReactNode;
};

export type TimelineGroupProps = {
  theme: Theme;
  // `description` is optional — see InfluencedByItem above.
  influencedBy: {
    positive: { label: string; description?: string }[];
    negative: { label: string; description?: string }[];
  };
  score: number;
  scoreUnit: string;
  scoreCategory: number;
  categoryColor: string;
  categoryLabel?: string;
  /** Which category scale `scoreCategory` belongs to — see CategoryPill. */
  categoryVariant?: CategoryVariant;
  metricId?: string;
  affectsItems: {
    positive: { label: string; description?: string }[];
    negative: { label: string; description?: string }[];
  };
  /**
   * When true, chip sections render as static labels — no onClick, no
   * cursor pointer, no description popup. Pass-through to the underlying
   * InfluencedBySection / AffectsThisSection. Used by the consultation
   * where descriptions aren't authored. Defaults to false.
   */
  readOnly?: boolean;
  /**
   * When true, the middle score node is omitted — only the "Influenced by"
   * and "Affects this" sections render (connected directly). Used by the
   * consultation for systems whose score is hidden (not fully approved), so
   * the relationships still show without exposing a score, and by the health
   * overview biomarker panel for qualitative / unrecorded biomarkers where
   * there is no numeric value to show. Defaults to false.
   */
  hideScore?: boolean;
};

export const TimelineGroup = ({
  theme,
  influencedBy,
  score,
  scoreUnit,
  scoreCategory,
  categoryColor,
  categoryLabel,
  categoryVariant = "score",
  metricId,
  affectsItems,
  readOnly = false,
  hideScore = false,
}: TimelineGroupProps) => {
  const hasInfluencedBy =
    influencedBy.positive.length > 0 || influencedBy.negative.length > 0;
  const hasAffects =
    affectsItems.positive.length > 0 || affectsItems.negative.length > 0;

  const rows: TimelineRow[] = [];

  if (hasInfluencedBy) {
    rows.push({
      marker: "icon",
      content: (
        <InfluencedBySection
          positive={influencedBy.positive}
          negative={influencedBy.negative}
          theme={theme}
          readOnly={readOnly}
        />
      ),
    });
  }

  if (!hideScore) {
    rows.push({
      marker: "score",
      categoryColor,
      content: (
        <ScoreRow
          score={score}
          unit={scoreUnit}
          scoreCategory={scoreCategory}
          categoryColor={categoryColor}
          categoryLabel={categoryLabel}
          categoryVariant={categoryVariant}
          metricId={metricId}
          theme={theme}
        />
      ),
    });
  }

  if (hasAffects) {
    rows.push({
      marker: "icon",
      content: (
        <AffectsThisSection
          positive={affectsItems.positive}
          negative={affectsItems.negative}
          theme={theme}
          readOnly={readOnly}
        />
      ),
    });
  }

  return (
    <div style={sharedStyles.timelineGrid}>
      {rows.map((row, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === rows.length - 1;

        const circle =
          row.marker === "icon" ? (
            <div style={sharedStyles.markerCircleIcon(theme)}>
              <DoubleArrowDown width={8} height={8} color="#3B3F42" />
            </div>
          ) : (
            <div style={sharedStyles.markerCircleScore(row.categoryColor!)} />
          );

        return (
          <React.Fragment key={idx}>
            {/* Marker cell */}
            <div style={sharedStyles.markerCell}>
              {!isFirst && (
                <div
                  style={sharedStyles.markerConnector(
                    theme,
                    row.marker === "score"
                  )}
                />
              )}
              {circle}
              <div style={sharedStyles.markerLineFill(theme)} />
            </div>

            {/* Content cell */}
            <div
              style={{
                paddingTop: isFirst ? 0 : 16,
                paddingBottom: isLast ? 0 : 16,
              }}
            >
              {row.content}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

// --- Styles ---
const sharedStyles = {
  // PanelHeader
  header: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  colorLine: (color: string) => ({
    width: "4px",
    alignSelf: "stretch" as const,
    borderRadius: "2px",
    backgroundColor: color,
    flexShrink: 0,
  }),
  headerTextSection: {
    width: "fit-content",
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  headerName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "24px",
    lineHeight: "1.4",
    letterSpacing: "-0.48px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  headerScoreRow: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  closeButton: {
    backgroundColor: "transparent",
    padding: "8px",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  // Value + category pill (shared by PanelHeader and ScoreRow). Matches the
  // health overview biomarker panel's header treatment: the value is plain
  // black text, the category sits on a tinted pill beside it.
  headerValue: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  scoreRowValue: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  categoryPill: (background: string, color: string) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "2px 8px",
    borderRadius: "4px",
    backgroundColor: background,
    color,
    whiteSpace: "nowrap" as const,
  }),

  // InfoTextBox
  infoTextBox: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
  }),
  infoTextBoxTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    letterSpacing: "-0.28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  infoTextBoxContent: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "8px 12px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),

  // TagChip
  tagChip: (bgColor: string) => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 8px",
    borderRadius: "4px",
    backgroundColor: bgColor,
  }),
  tagChipText: (textColor: string) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "11px",
    lineHeight: "1.4",
    letterSpacing: "-0.22px",
    color: textColor,
  }),

  // ScoreRow
  scoreRowContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },
  scoreRowLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "normal",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),

  // TagsSection
  tagsSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  tagsSectionHeader: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
  },
  tagsSectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    letterSpacing: "-0.28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  tagsSectionSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "11px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  tagsWrap: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "6px",
  },
  influencedByDescription: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "8px 12px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    letterSpacing: "-0.24px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    maxHeight: "120px",
    overflowY: "auto" as const,
  }),

  // ExpandableInfoCard
  expandableCard: (theme: Theme, isExpanded: boolean) => ({
    border: `1px solid ${
      isExpanded
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    padding: "12px",
    cursor: "pointer",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
    boxShadow: isExpanded
      ? "0px 8px 20px 0px rgba(0, 0, 0, 0.06), 0px 2px 8px 0px rgba(0, 0, 0, 0.04)"
      : "none",
  }),
  expandableCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "24px",
  },
  expandableDotAndText: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
  },
  expandableDot: (theme: Theme, isExpanded: boolean) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: isExpanded
      ? theme.colors_ds.PRIMARY_ICON_MAIN
      : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    flexShrink: 0,
  }),
  expandableTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  arrowIcon: {
    flexShrink: 0,
    transition: "transform 0.2s ease",
    display: "flex",
    alignItems: "center",
  },
  expandableDescription: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    paddingLeft: "14px",
  }),

  // SectionTitle
  sectionTitleWrap: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
  },
  sectionTitleText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "normal",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  sectionSubtitleText: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "normal",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),

  // ScoreMeaningSection / WeightCard
  compositionSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  compositionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "normal",
    letterSpacing: "-0.28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  cardsColumn: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  weightCard: (theme: Theme, isExpanded: boolean) => ({
    border: `1px solid ${
      isExpanded
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    padding: "12px",
    cursor: "pointer",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    boxShadow: isExpanded
      ? "0px 8px 20px 0px rgba(0, 0, 0, 0.06), 0px 2px 8px 0px rgba(0, 0, 0, 0.04)"
      : "none",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  }),
  weightCardInner: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
    padding: "8px 0",
  },
  weightCardContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  weightCardTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  weightName: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  weightPercentage: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  weightPercentageMax: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  progressBarBg: (theme: Theme) => ({
    width: "100%",
    height: "6px",
    backgroundColor: theme.colorTokens.P[10],
    borderRadius: "38px",
    overflow: "hidden" as const,
  }),
  progressBarFill: (theme: Theme, percentage: number) => ({
    width: `${percentage}%`,
    height: "100%",
    backgroundColor: theme.colors_ds.PRIMARY_GRAPH_MAIN,
    borderRadius: "38px",
  }),
  expandedContent: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  weightDescription: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  biomarkerList: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    overflow: "hidden" as const,
  }),
  biomarkerItem: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[30],
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  }),
  biomarkerInfo: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  biomarkerName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  biomarkerValue: (theme: Theme, color?: string) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: color || theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  biomarkerPercentage: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    flexShrink: 0,
  }),
  biomarkerSeparator: (theme: Theme) => ({
    height: "1px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  breathGroupCell: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.N[30],
    padding: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  }),
  breathGroupBiomarkers: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
  },
  breathGroupBiomarkerRow: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  insightCard: (theme: Theme, isExpanded: boolean) => ({
    border: `1px solid ${
      isExpanded
        ? theme.colors_ds.PRIMARY_ICON_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    borderRadius: "8px",
    padding: "8px 12px",
    cursor: "pointer",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
    boxShadow: isExpanded
      ? "0px 8px 20px 0px rgba(0, 0, 0, 0.06), 0px 2px 8px 0px rgba(0, 0, 0, 0.04)"
      : "none",
  }),
  insightCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  insightDotAndText: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
  },
  insightDot: (theme: Theme, isExpanded: boolean) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: isExpanded
      ? theme.colors_ds.PRIMARY_ICON_MAIN
      : theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
    flexShrink: 0,
  }),
  insightTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  insightDescription: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    paddingLeft: "14px",
  }),

  // TimelineGroup
  timelineGrid: {
    display: "grid",
    gridTemplateColumns: "16px 1fr",
    columnGap: "8px",
  },
  markerCell: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
  },
  markerConnector: (theme: Theme, isScore?: boolean) => ({
    width: "2px",
    height: isScore ? "19px" : "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
    flexShrink: 0,
  }),
  markerLineFill: (theme: Theme) => ({
    width: "2px",
    flex: 1,
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  markerCircleIcon: (theme: Theme) => ({
    width: "15px",
    height: "15px",
    borderRadius: "12px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_TEXT_DEFAULT}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  markerCircleScore: (color: string) => ({
    width: "15px",
    height: "15px",
    borderRadius: "12px",
    border: `1px solid ${color}`,
    backgroundColor: `${color}20`,
    flexShrink: 0,
  }),
};
